import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../constants';
import '../styles/Dashboard.css';
import { FaTachometerAlt, FaKey, FaProjectDiagram, FaSearch, FaCreditCard, FaSignOutAlt, FaUser } from 'react-icons/fa'; // Added FaUser
import { useTranslation } from 'react-i18next';

// Define icon components using react-icons
const IconProfile = () => <FaUser />;
const IconDashboard = () => <FaTachometerAlt />;
const IconApi = () => <FaKey />;
const IconV3 = () => <FaProjectDiagram />;
const IconV4 = () => <FaProjectDiagram />;
const IconSearch = () => <FaSearch />;
const IconBilling = () => <FaCreditCard />;
const IconLogout = () => <FaSignOutAlt />;

const API_URL = API_BASE_URL;

const Dashboard = () => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('dashboard'); // Default section
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('ampyfin_token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchDashboardData = async () => {
      setLoading(true);
      setError('');
      try {
        // Reverted back to /dashboard endpoint which seems more stable
        const response = await axios.get(`${API_URL}${API_ENDPOINTS.DASHBOARD}`, { 
          headers: {
            'token': token
          }
        });
        // Assuming /dashboard returns at least { email: ..., user_id: ... }
        setUserData(response.data); 
        // TODO: Fetch specific data based on the initial activeSection if needed

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (err.response?.status === 401) {
          localStorage.removeItem('ampyfin_token');
          navigate('/login');
        } else {
          // Add more specific error reporting if possible
          const errorDetail = err.response?.data?.detail || err.message;
          setError(t('dashboard.error', { errorDetail }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate, t]);

  const handleLogout = () => {
    localStorage.removeItem('ampyfin_token');
    navigate('/login');
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.profile.title')}</h2>
            <p>{t('dashboard.sections.profile.description')}</p>
            
            <div className="coming-soon">
              <h3>{t('dashboard.sections.profile.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.profile.comingSoon.description')}</p>
            </div>
          </div>
        );
      case 'dashboard':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.dashboard.title')}</h2>
            <p>{t('dashboard.sections.dashboard.description')}</p>
            
            <div className="coming-soon">
              <h3>{t('dashboard.sections.dashboard.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.dashboard.comingSoon.description')}</p>
            </div>
          </div>
        );
      case 'api':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.api.title')}</h2>
            <p>{t('dashboard.sections.api.description')}</p>
            <div className="coming-soon">
              <h3>{t('dashboard.sections.api.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.api.comingSoon.description')}</p>
            </div>
          </div>
        );
      case 'v3':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.v3.title')}</h2>
            <p>{t('dashboard.sections.v3.description')}</p>
            <div className="coming-soon">
              <h3>{t('dashboard.sections.v3.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.v3.comingSoon.description')}</p>
            </div>
          </div>
        );
      case 'v4':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.v4.title')}</h2>
            <p>{t('dashboard.sections.v4.description')}</p>
            <div className="coming-soon">
              <h3>{t('dashboard.sections.v4.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.v4.comingSoon.description')}</p>
            </div>
          </div>
        );
      case 'search':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.search.title')}</h2>
            <p>{t('dashboard.sections.search.description')}</p>
            <div className="coming-soon">
              <h3>{t('dashboard.sections.search.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.search.comingSoon.description')}</p>
            </div>
          </div>
        );
      case 'billing':
        return (
          <div className="section-container">
            <h2>{t('dashboard.sections.billing.title')}</h2>
            <p>{t('dashboard.sections.billing.description')}</p>
            <div className="coming-soon">
              <h3>{t('dashboard.sections.billing.comingSoon.title')}</h3>
              <p>{t('dashboard.sections.billing.comingSoon.description')}</p>
            </div>
          </div>
        );
      default:
        return <div>{t('dashboard.sections.default')}</div>;
    }
  };

  if (loading && !userData) { // Show loading only if no user data is present yet
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{t('dashboard.loading.dashboard')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="user-avatar">
            {userData?.email?.charAt(0).toUpperCase() || 'A'}
          </div>
          <div className="user-info">
            <p className="user-email">{userData?.email || 'user@example.com'}</p>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <ul>
            <li 
              className={activeSection === 'profile' ? 'active' : ''} 
              onClick={() => setActiveSection('profile')}
            >
              <span className="nav-icon"><IconProfile /></span>
              <span className="nav-text">{t('dashboard.sidebar.profile')}</span>
            </li>
            <li 
              className={activeSection === 'dashboard' ? 'active' : ''} 
              onClick={() => setActiveSection('dashboard')}
            >
              <span className="nav-icon"><IconDashboard /></span>
              <span className="nav-text">{t('dashboard.sidebar.dashboard')}</span>
            </li>
            <li 
              className={activeSection === 'api' ? 'active' : ''} 
              onClick={() => setActiveSection('api')}
            >
              <span className="nav-icon"><IconApi /></span>
              <span className="nav-text">{t('dashboard.sidebar.api')}</span>
            </li>
            <li 
              className={activeSection === 'v3' ? 'active' : ''} 
              onClick={() => setActiveSection('v3')}
            >
              <span className="nav-icon"><IconV3 /></span>
              <span className="nav-text">{t('dashboard.sidebar.v3')}</span>
            </li>
            <li 
              className={activeSection === 'v4' ? 'active' : ''} 
              onClick={() => setActiveSection('v4')}
            >
              <span className="nav-icon"><IconV4 /></span>
              <span className="nav-text">{t('dashboard.sidebar.v4')}</span>
            </li>
            <li 
              className={activeSection === 'search' ? 'active' : ''} 
              onClick={() => setActiveSection('search')}
            >
              <span className="nav-icon"><IconSearch /></span>
              <span className="nav-text">{t('dashboard.sidebar.search')}</span>
            </li>
            <li 
              className={activeSection === 'billing' ? 'active' : ''} 
              onClick={() => setActiveSection('billing')}
            >
              <span className="nav-icon"><IconBilling /></span>
              <span className="nav-text">{t('dashboard.sidebar.billing')}</span>
            </li>
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <span className="nav-icon"><IconLogout /></span>
            <span>{t('dashboard.sidebar.logout')}</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        {error && <div className="dashboard-error">{error}</div>}
        {loading ? (
          <div className="content-loading">
            <div className="spinner"></div>
            <p>{t('dashboard.loading.section')}</p>
          </div>
        ) : (
          renderSectionContent()
        )}
      </main>
    </div>
  );
};

export default Dashboard; 